/** Typed input at a point prompt: coordinates, distances, and the command's own keywords.
 *
 * ⚠⚠ **THE PROMPTS HAVE BEEN ASKING FOR THESE SINCE THE COMMAND LINE EXISTED.** `Specify next
 * point:` is an invitation to type `@3450<30`, and until now the only thing that could answer it
 * was the mouse — `AcadCommandLine.submit` runs every line through `resolveCommand`, so a typed
 * coordinate came back as *Unknown command "@3450<30"*. That is the same defect as a snap that
 * is lit and does nothing: the app asked a question it could not hear the answer to.
 *
 * ⚠ Everything here is a PURE FUNCTION over numbers, which is deliberate. The render gate and
 * `dsnshot` catch a drawing that looks wrong; neither can tell `@30,40` applied to the wrong
 * base point from one applied to the right one, because both put a line somewhere plausible.
 * Tested in `test/coords.test.ts` with `node --test`.
 *
 * ⚠ Angles are DEGREES, counter-clockwise from +X — AutoCAD's default and the drawing's own
 * convention. Nothing here knows about a UCS; when one exists it belongs at the call site,
 * transforming the point this returns, not in the parsing.
 */

export interface Pt { x: number; y: number }

export type TypedInput =
  /** A point the caller should place. `how` is for the transcript, not for behaviour. */
  | { kind: "point"; pt: Pt; how: "absolute" | "relative" | "polar" | "relative-polar"
                                 | "distance" | "last" }
  /** One of the keywords the live prompt offers, resolved to its canonical spelling. */
  | { kind: "keyword"; word: string }
  /** A bare number with no direction available — the caller decides what it means. */
  | { kind: "number"; value: number }
  /** Nothing usable. ⚠ Distinct from a MALFORMED coordinate, which is `error`. */
  | { kind: "none" }
  /** It looked like a coordinate and was not one. ⚠ Reported rather than silently ignored:
   *  swallowing `10,,4` places no point and looks exactly like a dead Enter key. */
  | { kind: "error"; why: string };

const DEG = Math.PI / 180;

/** The keywords a prompt offers, e.g. `Specify … or [3P/2P/Ttr (tan tan radius)]:` → three.
 *
 * ⚠ Read from the PROMPT STRING rather than a parallel table, because the prompt is what the
 * drafter is looking at. Two sources would drift, and the one that drifts silently is the table.
 */
export function keywordsOf(prompt: string): string[] {
  const m = /\[([^\]]+)\]/.exec(prompt || "");
  if (!m) return [];
  return m[1].split("/").map((w) => w.trim()).filter(Boolean);
}

/** The letters AutoCAD requires for a keyword — its capitals, in order.
 *
 * ⚠⚠ **THE CAPITALS ARE THE ABBREVIATION AND THEY ARE NOT ALWAYS THE FIRST LETTER.** CHAMFER
 * offers both `Multiple` and `mEthod`; typing `M` must mean Multiple and `E` must mean Method.
 * Taking the first letter would make one of them unreachable and the other ambiguous — and the
 * drafter would be typing at a prompt that names both.
 */
export function abbrevOf(keyword: string): string {
  // ⚠ The parenthesised gloss is dropped: `Ttr (tan tan radius)` abbreviates to `T`, and its
  // letters are for the human.
  // ⚠⚠ **THE CAPITALS ARE THE ABBREVIATION, WHEREVER IN THE KEYWORD THEY ARE — AutoCAD'S OWN
  // CONVENTION.** This used to look at the FIRST WORD only, which is right for every one-word
  // option and wrong for the shape AutoCAD uses to disambiguate: `[Angle/chord Length]` answers
  // to `L`, and `chord` is lower case precisely so that it does not. The first-word version
  // returned `C` — which is `Center` at the prompt next door.
  const head = keyword.trim().replace(/\(.*$/, "");
  const caps = head.replace(/[^A-Z0-9]/g, "");
  return caps || head.trim().slice(0, 1).toUpperCase();
}

/** Which keyword the drafter meant, or null.
 *
 * Accepts the abbreviation (`T`), the whole word (`TTR`), or a prefix of the whole word that
 * still starts with the abbreviation (`RA` for `Radius`). ⚠ A prefix that does NOT start with
 * the abbreviation is refused on purpose: `M` at CHAMFER's prompt is Multiple, never mEthod.
 */
export function matchKeyword(text: string, keywords: readonly string[]): string | null {
  const up = (text || "").trim().toUpperCase();
  if (!up) return null;
  // ⚠⚠⚠ **AN AMBIGUOUS ABBREVIATION IS REFUSED, NOT GUESSED.** CIRCLE offers both
  // `Ttr (tan tan radius)` and `Ttt (tan tan tan)`, and the capitals of both are just `T` —
  // BricsCAD has the same collision, spelling them `Tangent-tangent-radius` and
  // `Tangent-tangent-tangent`. Taking the first match means a drafter who types `T` meaning TTT
  // silently gets TTR: a command that answers and does the WRONG thing, which is worse than one
  // that does nothing. Only an EXACT hit — the abbreviation, the whole word or the first word —
  // can be shared, and it cannot: exact hits are unique by construction.
  const exact = keywords.filter((kw) => {
    const word = kw.trim().replace(/\(.*$/, "").trim().toUpperCase();
    return up === abbrevOf(kw) || up === word || up === (word.split(/\s/)[0] ?? "");
  });
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;
  const pre = keywords.filter((kw) => {
    const word = kw.trim().replace(/\(.*$/, "").trim().toUpperCase();
    return word.startsWith(up) && up.startsWith(abbrevOf(kw));
  });
  if (pre.length > 1) return null;
  for (const kw of keywords) {
    const ab = abbrevOf(kw);
    // ⚠ The whole keyword, gloss dropped — `CHORD LENGTH` is a thing a drafter may well type in
    // full, and matching only the first word would refuse the name the prompt printed.
    const word = kw.trim().replace(/\(.*$/, "").trim().toUpperCase();
    const first = word.split(/\s/)[0] ?? "";
    if (up === ab || up === word || up === first) return kw;
    // ⚠ A PREFIX is accepted only when it starts with the abbreviation: `RA` is Radius, and `M`
    // at CHAMFER's prompt is Multiple and never mEthod.
    if (word.startsWith(up) && up.startsWith(ab)) return kw;
  }
  return null;
}

/** Every keyword an abbreviation could mean. ⚠ Empty or single-element unless the prompt really
 *  is ambiguous — `matchKeyword` refuses in exactly the cases this returns more than one. */
export function ambiguousKeywords(text: string, keywords: readonly string[]): string[] {
  const up = (text || "").trim().toUpperCase();
  if (!up) return [];
  const word = (kw: string) => kw.trim().replace(/\(.*$/, "").trim().toUpperCase();
  const exact = keywords.filter((kw) => up === abbrevOf(kw) || up === word(kw)
                                     || up === (word(kw).split(/\s/)[0] ?? ""));
  if (exact.length) return exact;
  return keywords.filter((kw) => word(kw).startsWith(up) && up.startsWith(abbrevOf(kw)));
}

function num(tok: string): number | null {
  const t = tok.trim();
  // ⚠ `Number("")` is 0 and `Number(" ")` is 0. An empty ordinate is a typo, not the origin.
  if (!t) return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

/** Parse what the drafter typed while a command is asking for a point.
 *
 * @param last   the previous point, for `@` forms and direct distance entry
 * @param cursor where the pointer is, for direct distance entry — already constrained by
 *               ORTHO/POLAR if those are on, because the direction it gives IS the answer
 */
export function parseTyped(
  text: string,
  ctx: { last?: Pt | null; cursor?: Pt | null; keywords?: readonly string[] } = {},
): TypedInput {
  const raw = (text ?? "").trim();
  if (!raw) return { kind: "none" };

  const kw = ctx.keywords?.length ? matchKeyword(raw, ctx.keywords) : null;
  if (kw) return { kind: "keyword", word: kw };
  // ⚠⚠ **SAY WHICH ONES, OR THE REFUSAL IS INDISTINGUISHABLE FROM A TYPO.** `T` on CIRCLE's
  // prompt matches both tangent options; without this it falls through to the coordinate parser
  // and comes back "that is not a point", which sends the drafter looking for the wrong problem.
  const amb = ctx.keywords?.length ? ambiguousKeywords(raw, ctx.keywords) : [];
  if (amb.length > 1) {
    return { kind: "error",
             why: `${raw.trim().toUpperCase()} could be ${amb.join(" or ")} — type more of it` };
  }

  const rel = raw.startsWith("@");
  const body = (rel ? raw.slice(1) : raw).trim();
  const last = ctx.last ?? null;

  // `@` alone — the last point again. AutoCAD's way of starting exactly where you stopped.
  if (rel && !body) {
    return last ? { kind: "point", pt: { ...last }, how: "last" }
                : { kind: "error", why: "there is no previous point" };
  }
  if (rel && !last) return { kind: "error", why: "there is no previous point" };

  // polar: `d<a`
  const lt = body.indexOf("<");
  if (lt >= 0) {
    const d = num(body.slice(0, lt));
    const a = num(body.slice(lt + 1));
    if (d === null || a === null) return { kind: "error", why: "expected distance<angle" };
    const dx = d * Math.cos(a * DEG);
    const dy = d * Math.sin(a * DEG);
    return rel
      ? { kind: "point", pt: { x: last!.x + dx, y: last!.y + dy }, how: "relative-polar" }
      : { kind: "point", pt: { x: dx, y: dy }, how: "polar" };
  }

  // cartesian: `x,y` or `x,y,z`
  if (body.includes(",")) {
    const parts = body.split(",");
    if (parts.length > 3) return { kind: "error", why: "too many ordinates" };
    const x = num(parts[0]);
    const y = num(parts[1]);
    // ⚠ Z is accepted and DROPPED. Refusing it would reject a perfectly ordinary AutoCAD
    // keystroke; pretending to honour it in a 2D drawing would be worse.
    if (x === null || y === null) return { kind: "error", why: "expected x,y" };
    if (parts.length === 3 && num(parts[2]) === null) {
      return { kind: "error", why: "expected x,y,z" };
    }
    return rel
      ? { kind: "point", pt: { x: last!.x + x, y: last!.y + y }, how: "relative" }
      : { kind: "point", pt: { x, y }, how: "absolute" };
  }

  // a bare number — DIRECT DISTANCE ENTRY, the one a drafter uses most.
  const d = num(body);
  if (d === null) return { kind: "none" };
  if (!last || !ctx.cursor) return { kind: "number", value: d };
  const vx = ctx.cursor.x - last.x;
  const vy = ctx.cursor.y - last.y;
  const len = Math.hypot(vx, vy);
  // ⚠ With the pointer sitting exactly on the last point there is no direction, and picking one
  // (+X, say) would send a wall off in a direction nobody indicated.
  if (len < 1e-9) return { kind: "number", value: d };
  return { kind: "point", how: "distance",
           pt: { x: last.x + (vx / len) * d, y: last.y + (vy / len) * d } };
}

/** Where the cursor really is once ORTHO, POLAR and grid SNAP have had their say.
 *
 * ⚠⚠ **F8, F9 AND THE POLAR DROP-UP HAVE BEEN LIT AND INERT.** `tog.ortho`, `tog.polar` and
 * `tog.snapmode` were published to the status bar, drawn as pressed, saved per drawing — and
 * read by nothing. Same defect as the OSNAP drop-up that listed fourteen snaps and performed
 * none: a drafter turns ORTHO on, trusts the line is horizontal, and finds out at the plotter.
 *
 * ⚠ Order matters and is AutoCAD's: grid snap rounds the raw pointer first, then ORTHO or POLAR
 * constrains the DIRECTION from the last point. Doing it the other way round rounds an
 * already-constrained point back off its own axis.
 * ⚠ ORTHO beats POLAR when both are somehow on — AutoCAD's own precedence, and the reason its
 * status bar turns one off when you press the other.
 */
export function constrain(
  cursor: Pt,
  last: Pt | null,
  opt: { ortho?: boolean; polar?: boolean; polarAng?: number;
         snap?: boolean; snapX?: number; snapY?: number } = {},
): Pt {
  let p = { x: cursor.x, y: cursor.y };
  const sx = opt.snapX ?? 10;
  const sy = opt.snapY ?? sx;
  if (opt.snap && sx > 0 && sy > 0) {
    p = { x: Math.round(p.x / sx) * sx, y: Math.round(p.y / sy) * sy };
  }
  if (!last) return p;
  const dx = p.x - last.x;
  const dy = p.y - last.y;
  if (opt.ortho) {
    // ⚠ The LONGER axis wins, which is what makes ortho feel like a hinge rather than a jump.
    return Math.abs(dx) >= Math.abs(dy)
      ? { x: p.x, y: last.y }
      : { x: last.x, y: p.y };
  }
  if (opt.polar) {
    const step = opt.polarAng && opt.polarAng > 0 ? opt.polarAng : 90;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return p;
    const a = Math.round((Math.atan2(dy, dx) / DEG) / step) * step;
    return { x: last.x + len * Math.cos(a * DEG), y: last.y + len * Math.sin(a * DEG) };
  }
  return p;
}

// ── the UCS ─────────────────────────────────────────────────────────────────────────────────
/** A user coordinate system, which in a 2D drawing is an ORIGIN and a ROTATION and nothing else.
 *
 * ⚠⚠ **AutoCAD's UCS COMMAND ASKS THREE QUESTIONS AND ONLY TWO OF THEM EXIST HERE.** "Specify
 * origin", "Specify point on X-axis" and "Specify point on the XY plane" — the third names the
 * plane's tilt, and this canvas is one plane. Storing a full 3D matrix to keep two of its nine
 * numbers honest would be a lie about what the app can do with the other seven.
 *
 * ⚠ `rot` is RADIANS, counter-clockwise, matching `Math.atan2`. Everything a drafter SEES is
 * degrees; the conversion happens at the edge, once, so nothing in here has to remember which.
 */
export interface Ucs { x: number; y: number; rot: number }

/** The world. ⚠ A constant rather than a literal at each site: `WORLD_UCS` compares by identity
 *  in the "are we in the world?" checks, and three separate `{x:0,y:0,rot:0}` would not. */
export const WORLD_UCS: Ucs = { x: 0, y: 0, rot: 0 };

/** True when `u` IS the world, whatever object it is. ⚠ Not `u === WORLD_UCS`: a restored named
 *  origin of 0,0,0 is the world too, and the icon and the status bar must agree it is. */
export function isWorldUcs(u: Ucs | null | undefined): boolean {
  return !u || (Math.abs(u.x) < 1e-9 && Math.abs(u.y) < 1e-9 && Math.abs(u.rot) < 1e-12);
}

/** A point given in the UCS, expressed in the world. */
export function ucsToWorld(p: Pt, u: Ucs): Pt {
  if (isWorldUcs(u)) return { x: p.x, y: p.y };
  const c = Math.cos(u.rot), s = Math.sin(u.rot);
  return { x: u.x + p.x * c - p.y * s, y: u.y + p.x * s + p.y * c };
}

/** A world point expressed in the UCS — the inverse, and what the coordinate box shows. */
export function worldToUcs(p: Pt, u: Ucs): Pt {
  if (isWorldUcs(u)) return { x: p.x, y: p.y };
  const c = Math.cos(u.rot), s = Math.sin(u.rot);
  const dx = p.x - u.x, dy = p.y - u.y;
  return { x: dx * c + dy * s, y: -dx * s + dy * c };
}

/** The UCS named by AutoCAD's two picks: the new origin, and any point on its +X axis.
 *
 * ⚠ A second pick ON the origin leaves the rotation ALONE rather than producing NaN from
 * `atan2(0, 0)` — which is 0, silently un-rotating an axis the drafter never asked to change.
 */
export function ucsFromPoints(origin: Pt, onX: Pt | null, was?: Ucs): Ucs {
  const dx = onX ? onX.x - origin.x : 0;
  const dy = onX ? onX.y - origin.y : 0;
  const rot = Math.hypot(dx, dy) < 1e-9 ? (was?.rot ?? 0) : Math.atan2(dy, dx);
  return { x: origin.x, y: origin.y, rot };
}
